<template>
<div v-if="data" class="chart-group">
  <div>
    <dimension-weather-chart
      v-if="hasAllWindFactor"
      ref="chart"
      :data-config="data"
      :dimension="0"
      :unit-prop="unitProp"
      download-name="综合分析"
      chart-min-height="300px"
    >
      <template #toolbar-after>
        <el-button size="small" @click="onSelectFactorBtnClick">选择因子</el-button>
      </template>
    </dimension-weather-chart>
    <x-y-chart
      v-else
      ref="chart"
      :data-config="data"
      :unit-prop="unitProp"
      chart-type="line"
      :stack-type="1"
      download-name="综合分析"
      chart-min-height="300px"
      chart-height="auto"
      selected-change-with-data-changed
      need-resize
    >
      <template #toolbar-after>
        <el-button size="small" @click="onSelectFactorBtnClick">选择因子</el-button>
      </template>
    </x-y-chart>
  </div>

  <el-dialog
    title="选择因子"
    :visible.sync="visible"
    :append-to-body="true"
    custom-class="custom-dialog"
    width="max-content"
  >
    <el-cascader-panel
      :key="site.id"
      v-model="factorIds"
      size="small"
      :options="devices"
      :show-all-levels="false"
      :props="{
        value: 'id',
        label: 'name',
        multiple: true,
        emitPath: false
      }"
      collapse-tags
    />

    <template slot="footer">
      <el-button type="orange" style="width:100px;" @click="onOkBtnClick">确定</el-button>
      <el-button style="width:100px;" @click="onCancelBtnClick">取消</el-button>
    </template>
  </el-dialog>
</div>
</template>

<script>
import BaseStore from '../../../../components/BaseStore'
import DataFormatter from '../../../../model/DataFormatter'
import DimensionWeatherChart from '../../../../components/Data/ChartWrapper/DimensionWeatherChart'
import XYChart from '../../../../components/DataChart/XYChart'
import Constant from '../../../../model/Constant'

export default {
  name: 'ChartGroup',
  extends: BaseStore,
  components: {XYChart, DimensionWeatherChart},
  props: {
    site: {
      required: true,
      type: Object
    },
    defaultFactorIds: {
      required: true,
      type: Array
    },
    unitProp: {
      required: true,
      type: String
    },
    query: {
      required: true,
      type: Object
    },
    siteFactorMap: {
      required: true,
      type: Object
    },
    factorSiteMap: {
      required: true,
      type: Object
    },
    completeAllData: {
      required: true,
      type: Boolean
    },
    dimension: {
      required: true,
      type: Number
    },
    filterSiteMap: {
      required: true,
      type: Object
    }
  },
  data: () => ({
    visible: false,
    factorIds: [],
    data: null,
    noDataList: Object.freeze([])
  }),
  computed: {
    hasAllWindFactor () {
      const windFactorIds = this.data.dimensions.reduce((list, dimension) => {
        const {factorId} = dimension

        if (/^(1012|1013)$/.test(factorId)) {
          list.push(factorId)
        }

        return list
      }, [])

      return windFactorIds.includes('1012') && windFactorIds.includes('1013')
    },
    devices () {
      return JSON.parse(JSON.stringify(this.site.devices)).map(device => ({
        id: device.id,
        name: device.name,
        children: Object.values(device.siteDeviceFactors.reduce((map, factor) => {
          const {factorId} = factor
          map[factorId] = {
            id: factorId,
            name: factor.name
          }

          return map
        }, {}))
      }))
    },
    noDataReasonMap () {
      return this.noDataList.reduce((map, noData) => {
        map[`${noData.siteId}-${noData.siteDeviceId}-${noData.dataTime}:00:00`] = noData
        return map
      }, {})
    }
  },
  methods: {
    init () {
      this.currentQuery = {
        ...this.query,
        factorIds: this.factorIds = this.defaultFactorIds
      }

      this.getData()
    },
    getData () {
      this.changeLoadStatus(true)

      const {currentQuery} = this

      Promise.all([
        this.$api.Data.getHistoryData(currentQuery),
        this.$api.NoData.getNoDataListBySite(currentQuery)
      ]).then(this.setData).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    setData ([dataList, noDataList]) {
      const {NO_DATA_REASON_MAP} = Constant

      this.noDataList = Object.freeze(noDataList.map(noData => {
        let {cause} = noData
        const noDataReasons = []

        while (cause > 0) {
          const bin = -cause & cause
          cause ^= bin
          const noDataReason = NO_DATA_REASON_MAP[bin]

          if (noDataReason) {
            noDataReasons.push(noDataReason.label)
          }
        }

        return {
          ...noData,
          reason: noDataReasons.join(',')
        }
      }))

      this.tmpList = Object.freeze(DataFormatter.formatWeatherDataList.bind(this)(dataList))
      this.setFormatData()

      setTimeout(() => {
        this.$emit('rendered')
      }, 0)
    },
    setFormatData () {
      this.formatData()
      this.data = Object.freeze(this.formatVOCsData())
    },
    formatData: DataFormatter.format2,
    formatVOCsData () {
      const {dataList, factorGroupMap} = this
      const [dataConfig] = dataList
      const dimensions = []
      const vocsDimensions = []
      const vocsGroupMap = dataConfig.dimensions.reduce((map, factor) => {
        if (factor.rootFactorId === 'VOC') {
          const {parentId} = factor

          if (!map[parentId]) {
            const factorGroup = factorGroupMap[parentId]

            if (factorGroup) {
              map[parentId] = {
                ...factor,
                ...factorGroup,
                factorId: parentId,
                seriesParam: {
                  type: 'line',
                  showSymbol: false,
                  areaStyle: {},
                  stack: 'VOCs'
                }
              }
            }
          }

          vocsDimensions.push(factor)
        } else {
          dimensions.push(factor)
        }

        return map
      }, {})

      const vocsGroups = Object.values(vocsGroupMap)

      if (vocsGroups.length === 0) {
        return dataConfig
      }

      const {seriesMap, dataTimes} = dataConfig
      const newSeriesMap = vocsGroups.reduce((map, vocsGroup) => {
        map[vocsGroup.factorId] = {}
        return map
      }, dimensions.reduce((map, dimension) => {
        const {factorId} = dimension
        map[factorId] = seriesMap[factorId]
        return map
      }, {}))

      return {
        ...dataConfig,
        dimensions: vocsGroups.concat(dimensions),
        seriesMap: vocsDimensions.reduce((map, dimension) => {
          const {parentId, factorId} = dimension
          const dataMap = seriesMap[factorId]
          const newDataMap = map[parentId]

          return dataTimes.reduce((map, dataTime) => {
            dataTime = dataTime[1]
            const oldData = dataMap[dataTime]

            if (oldData) {
              const { value } = oldData
              if (Number.isFinite(value)) {
                let newData = newDataMap[dataTime]

                if (!newData) {
                  newData = newDataMap[dataTime] = {
                    ...oldData,
                    value: 0
                  }
                }

                newData.value += value
              }
            }

            return map
          }, map)
        }, newSeriesMap)
      }
    },
    onSelectFactorBtnClick () {
      this.visible = true
    },
    onOkBtnClick () {
      this.currentQuery.factorIds = this.factorIds
      this.getData()
      this.visible = false
    },
    onCancelBtnClick () {
      this.visible = false
    },
    getChartComponent () {
      return this.$refs.chart.getChartComponent()
    }
  },
  watch: {
    site () {
      this.init()
    },
    query () {
      this.currentQuery = {
        ...this.query,
        factorIds: this.factorIds
      }
      this.getData()
    },
    unitProp () {
      this.setFormatData()
    }
  },
  created () {
    this.init()
  }
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/01Photochemical/06ComplexAnalysis/ChartGroup.vue